/**
 * Question Type Definitions and Icons
 */

import {
  AtSymbolIcon,
  PhoneIcon,
  CalendarIcon,
  Bars3BottomLeftIcon,
  ChevronDownIcon,
  Squares2X2Icon,
  StarIcon,
  CurrencyDollarIcon,
  UserIcon
} from '@heroicons/react/24/outline';

export const QUESTION_TYPES = [
  {
    id: 'text',
    label: 'Short Text',
    description: 'Single line text input',
    icon: UserIcon,
    defaultLabel: 'What is your name?',
    defaultPlaceholder: 'Enter your name...',
    aiContexts: ['name', 'company', 'general']
  },
  {
    id: 'email',
    label: 'Email',
    description: 'Email address field',
    icon: AtSymbolIcon,
    defaultLabel: 'What is your email?',
    defaultPlaceholder: 'your@email.com',
    aiContexts: ['email'],
    validation: 'email'
  },
  {
    id: 'phone',
    label: 'Phone',
    description: 'Phone number field',
    icon: PhoneIcon,
    defaultLabel: 'What is your phone number?',
    defaultPlaceholder: '(555) 123-4567',
    aiContexts: ['phone'],
    validation: 'phone'
  },
  {
    id: 'textarea',
    label: 'Long Text',
    description: 'Multi-line text area',
    icon: Bars3BottomLeftIcon,
    defaultLabel: 'Tell us about your project',
    defaultPlaceholder: 'Share your vision with us...',
    aiContexts: ['project_vision', 'notes', 'general']
  },
  {
    id: 'date',
    label: 'Date',
    description: 'Date picker',
    icon: CalendarIcon,
    defaultLabel: 'When is your event?',
    defaultPlaceholder: 'Select a date',
    aiContexts: ['event_date', 'deadline', 'general']
  },
  {
    id: 'select',
    label: 'Dropdown',
    description: 'Single choice from list',
    icon: ChevronDownIcon,
    defaultLabel: 'How did you hear about us?',
    defaultPlaceholder: 'Select an option',
    aiContexts: ['referral_source', 'project_type', 'general'],
    hasOptions: true,
    defaultOptions: [
      { value: 'google', label: 'Google Search', score: 50 },
      { value: 'instagram', label: 'Instagram', score: 60 },
      { value: 'referral', label: 'Friend/Family Referral', score: 80 },
      { value: 'wedding_wire', label: 'Wedding Wire', score: 70 },
      { value: 'other', label: 'Other', score: 40 }
    ]
  },
  {
    id: 'multiselect',
    label: 'Checkboxes',
    description: 'Multiple choices allowed',
    icon: Squares2X2Icon,
    defaultLabel: 'What services are you interested in?',
    defaultPlaceholder: 'Select all that apply',
    aiContexts: ['services_interest', 'preferences', 'general'],
    hasOptions: true,
    defaultOptions: [
      { value: 'photography', label: 'Photography', score: 50 },
      { value: 'videography', label: 'Videography', score: 50 },
      { value: 'both', label: 'Photo + Video Package', score: 80 }
    ]
  },
  {
    id: 'rating',
    label: 'Rating',
    description: 'Scale rating (1-5)',
    icon: StarIcon,
    defaultLabel: 'How soon do you need to book?',
    aiContexts: ['urgency', 'satisfaction', 'general'],
    hasScale: true
  },
  {
    id: 'budget',
    label: 'Budget Range',
    description: 'Predefined budget options',
    icon: CurrencyDollarIcon,
    defaultLabel: 'What is your budget range?',
    aiContexts: ['budget'],
    hasOptions: true,
    defaultOptions: [
      { value: 'under_1000', label: 'Under $1,000', score: 20 },
      { value: '1000_3000', label: '$1,000 - $3,000', score: 50 },
      { value: '3000_5000', label: '$3,000 - $5,000', score: 80 },
      { value: '5000_plus', label: '$5,000+', score: 100 }
    ]
  }
];

// Pre-made form templates for common photography use cases
export const FORM_TEMPLATES = [
  {
    id: 'wedding',
    name: 'Wedding Inquiry',
    description: 'Perfect for wedding photography leads',
    questions: [
      { type: 'text', label: 'What are your names?', placeholder: 'e.g., Sarah & John', required: true, aiWeight: 'medium', aiContext: 'name' },
      { type: 'email', label: 'What is your email?', placeholder: 'your@email.com', required: true, aiWeight: 'high', aiContext: 'email' },
      { type: 'phone', label: 'What is your phone number?', placeholder: '(555) 123-4567', required: false, aiWeight: 'medium', aiContext: 'phone' },
      { type: 'date', label: 'When is your wedding date?', placeholder: 'Select a date', required: true, aiWeight: 'high', aiContext: 'event_date' },
      { type: 'text', label: 'Where is your venue?', placeholder: 'Venue name or location', required: true, aiWeight: 'medium', aiContext: 'general' },
      { type: 'budget', label: 'What is your photography budget?', required: true, aiWeight: 'high', aiContext: 'budget' },
      { type: 'select', label: 'How did you find us?', placeholder: 'Select an option', required: false, aiWeight: 'low', aiContext: 'referral_source', options: [
        { value: 'google', label: 'Google Search', score: 50 },
        { value: 'instagram', label: 'Instagram', score: 60 },
        { value: 'referral', label: 'Friend/Family Referral', score: 80 },
        { value: 'wedding_wire', label: 'Wedding Wire / The Knot', score: 70 },
        { value: 'other', label: 'Other', score: 40 }
      ]},
      { type: 'textarea', label: 'Tell us about your wedding vision', placeholder: 'Share the style, mood, or any special moments you want captured...', required: false, aiWeight: 'medium', aiContext: 'project_vision' }
    ]
  },
  {
    id: 'portrait',
    name: 'Portrait Session',
    description: 'For headshots, family, or personal portraits',
    questions: [
      { type: 'text', label: 'What is your name?', placeholder: 'Your full name', required: true, aiWeight: 'medium', aiContext: 'name' },
      { type: 'email', label: 'What is your email?', placeholder: 'your@email.com', required: true, aiWeight: 'high', aiContext: 'email' },
      { type: 'phone', label: 'What is your phone number?', placeholder: '(555) 123-4567', required: false, aiWeight: 'medium', aiContext: 'phone' },
      { type: 'select', label: 'What type of session?', placeholder: 'Select session type', required: true, aiWeight: 'high', aiContext: 'project_type', options: [
        { value: 'headshot', label: 'Professional Headshot', score: 70 },
        { value: 'family', label: 'Family Portrait', score: 60 },
        { value: 'couple', label: 'Couple / Engagement', score: 80 },
        { value: 'personal', label: 'Personal Branding', score: 90 },
        { value: 'other', label: 'Other', score: 50 }
      ]},
      { type: 'date', label: 'When would you like to schedule?', placeholder: 'Preferred date', required: false, aiWeight: 'medium', aiContext: 'event_date' },
      { type: 'budget', label: 'What is your budget range?', required: true, aiWeight: 'high', aiContext: 'budget' },
      { type: 'textarea', label: 'Any specific ideas or locations in mind?', placeholder: 'Share your vision for the session...', required: false, aiWeight: 'low', aiContext: 'project_vision' }
    ]
  },
  {
    id: 'event',
    name: 'Event Photography',
    description: 'Corporate events, parties, conferences',
    questions: [
      { type: 'text', label: 'What is your name?', placeholder: 'Your full name', required: true, aiWeight: 'medium', aiContext: 'name' },
      { type: 'email', label: 'What is your email?', placeholder: 'your@email.com', required: true, aiWeight: 'high', aiContext: 'email' },
      { type: 'phone', label: 'What is your phone number?', placeholder: '(555) 123-4567', required: false, aiWeight: 'medium', aiContext: 'phone' },
      { type: 'text', label: 'Company or organization name?', placeholder: 'Company name (if applicable)', required: false, aiWeight: 'medium', aiContext: 'company' },
      { type: 'select', label: 'What type of event?', placeholder: 'Select event type', required: true, aiWeight: 'high', aiContext: 'project_type', options: [
        { value: 'corporate', label: 'Corporate Event', score: 90 },
        { value: 'conference', label: 'Conference / Summit', score: 85 },
        { value: 'party', label: 'Party / Celebration', score: 60 },
        { value: 'nonprofit', label: 'Non-Profit / Fundraiser', score: 70 },
        { value: 'other', label: 'Other', score: 50 }
      ]},
      { type: 'date', label: 'When is the event?', placeholder: 'Event date', required: true, aiWeight: 'high', aiContext: 'event_date' },
      { type: 'text', label: 'Expected number of guests?', placeholder: 'e.g., 50-100', required: false, aiWeight: 'medium', aiContext: 'general' },
      { type: 'budget', label: 'What is your budget range?', required: true, aiWeight: 'high', aiContext: 'budget' },
      { type: 'textarea', label: 'Tell us more about your event', placeholder: 'Location, duration, special requirements...', required: false, aiWeight: 'medium', aiContext: 'project_vision' }
    ]
  },
  {
    id: 'general',
    name: 'General Inquiry',
    description: 'Simple contact form for any service',
    questions: [
      { type: 'text', label: 'What is your name?', placeholder: 'Your full name', required: true, aiWeight: 'medium', aiContext: 'name' },
      { type: 'email', label: 'What is your email?', placeholder: 'your@email.com', required: true, aiWeight: 'high', aiContext: 'email' },
      { type: 'phone', label: 'What is your phone number?', placeholder: '(555) 123-4567', required: false, aiWeight: 'medium', aiContext: 'phone' },
      { type: 'textarea', label: 'How can we help you?', placeholder: 'Tell us what you\'re looking for...', required: true, aiWeight: 'medium', aiContext: 'project_vision' }
    ]
  }
];

export const AI_CONTEXTS = {
  name: { label: 'Name', description: 'Lead\'s name for personalization' },
  email: { label: 'Email', description: 'Primary contact email' },
  phone: { label: 'Phone', description: 'Contact phone number' },
  event_date: { label: 'Event Date', description: 'Date of event/project' },
  budget: { label: 'Budget', description: 'Budget range for scoring' },
  referral_source: { label: 'Referral Source', description: 'How they found you' },
  project_type: { label: 'Project Type', description: 'Type of project/service' },
  services_interest: { label: 'Services', description: 'Interested services' },
  project_vision: { label: 'Vision', description: 'Their vision/requirements' },
  urgency: { label: 'Urgency', description: 'How urgent their timeline is' },
  general: { label: 'General', description: 'General information' }
};

export const AI_WEIGHTS = [
  { value: 'high', label: 'High Impact', description: 'Strongly affects lead score' },
  { value: 'medium', label: 'Medium Impact', description: 'Moderately affects lead score' },
  { value: 'low', label: 'Low Impact', description: 'Slightly affects lead score' },
  { value: 'none', label: 'No Impact', description: 'Not used for scoring' }
];

export const getQuestionType = (typeId) => {
  return QUESTION_TYPES.find(t => t.id === typeId);
};
